import angular from "angular";

import { AccountSettingsInSession, ContactType, Country, Employment, EmploymentListItem, EmploymentLogic, EmploymentRelationTaxcard, EmploymentRelationType, EmploymentSocialSecurityModel, Employments, InsuranceCompany, InternationalSocialSecurityException, PensionCalculation, PensionCompany, TaxCard2019Logic, Taxcard, TaxcardKind, UserSession, Validation } from "@salaxy/core";

import { EditDialogParameters, SessionService, UiHelpers, WizardService, WizardStep } from "../../services";
import { WizardController } from "../bases";

/**
 * Wizard for creating a new employment relation.
 */
export class EmploymentWizardController extends WizardController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$scope", "EditDialogParameters", "WizardService", "SessionService", "UiHelpers", "Employments"];

  /** Form data validity: This is bound from the form tag in the view. */
  public formDataValidity: boolean;

  /** The employment relation that is being added. */
  public current: Employment;

  /** Taxcard that is optionally added. */
  public taxcard: Taxcard;

  /**
   * Creates a new WizardController with dependency injection.
   * @ignore
   */
  constructor(
    $scope: angular.IScope,
    private editDialogParameters: EditDialogParameters<Employment>,
    wizardService: WizardService,
    private sessionService: SessionService,
    private uiHelpers: UiHelpers,
    private api: Employments,
  ) {
    super($scope, wizardService);
  }

  /**
   * Initialization of the controller
   */
  public $onInit() {
    super.$onInit();
    this.wizardService.setSteps(this.getWizardSteps());
    this.wizardService.activeStepNumber = 1;
    if (!this.editDialogParameters) {
      throw new Error("No editDialogParameters coming in.");
    }
    this.current = this.editDialogParameters.current;
  }

  /** Returns true if user can go forward in the wizard  */
  public get canGoNext(): boolean {
    if (this.steps.length > this.step) {
      if (this.steps[this.step] && !this.steps[this.step].disabled) {
        if (this.formDataValidity === true || this.formDataValidity === null) {
          return true;
        }
      }
    }
    return false;
  }

  /** Gets the session - currently used for company type */
  public get session(): UserSession {
    return this.sessionService.session;
  }

  /** Called when the type of employment changes => Sets the income earner types accordingly. */
  public employmentTypeChange(): void {
    EmploymentLogic.resetModelFromType(this.current);
    EmploymentLogic.setSalaryDefaultRows(this.current.info.type, this.current.work);
  }

  /** Gets the cached version of the social security selection. */
  public get socSec() {
    return this.uiHelpers.cache(this, "socSec",
      () => this.getSocialSecurityModel(
        this.sessionService.session?.settings, this.current.info.type, this.current.international.socialSecurity, this.current.info.pensionCalculation,
        this.current.info.pensionCompany, this.current.info.insuranceCompany, this.current.international.socialSecurityExceptions),
      () => this.current.info.type + this.current.international.socialSecurity + this.current.info.pensionCalculation
        + this.current.info.pensionCompany + this.current?.info.insuranceCompany + this.current.international.socialSecurityExceptions
    );
  }

  /** Gets or sets the current country. When setting, the "foreign" address is reset. When getting Finland (fi) is converted to null. */
  public get country(): Country | null {
    return this.current.international.country == Country.Fi ? null : this.current.international.country;
  }
  public set country(value: Country) {
    this.current.international.country = value;
    this.current.contacts = (this.current.contacts || []).filter(x => x.type != ContactType.Foreign);
    const contact = this.getContact(ContactType.Foreign);
    contact.country = value;
  }

  /**
   * Gets the social security pensions model (available options) for the current selections.
   * @param settings Account settings in the session
   * @param type Type of employment relation selected
   * @param socialSecurity Whether the person is in Finnish or foregion social security - or mixed
   * @param pensionCalculation Type of Pension calculation: employee/TyEL or others (YEL, farmer etc.)
   * @param pensionCompany Pension company (if several) that is selected.
   * @param insuranceCompany Insurance company (if several) that is selected.
   * @param socSecExceptions The selected social security exceptions (not subject to)
   */
  public getSocialSecurityModel(
    settings: AccountSettingsInSession, type: EmploymentRelationType,
    socialSecurity: "default" | "foreign" | "mixed", pensionCalculation: PensionCalculation,
    pensionCompany: PensionCompany, insuranceCompany: InsuranceCompany,
    socSecExceptions: InternationalSocialSecurityException[],
  ): EmploymentSocialSecurityModel {
    return EmploymentLogic.getSocialSecurityModel(settings, type, socialSecurity, pensionCalculation, pensionCompany, insuranceCompany, socSecExceptions);
  }

  /**
   * Called when the type of pension calculation changes
   * => Sets the PartialOwner and NoPensionRequired IncomeEarnerTypes accordingly.
   */
  public pensionCalculationChange() {
    EmploymentLogic.resetModelFromPensionCalculation(this.current);
  }

  private _otherEmployments: EmploymentListItem[] | "loading" | undefined;

  private _otherEmploymentsLastPersonalId: string | undefined;

  /**
   * Gets the list of other employments with this personal ID.
   * First call starts the loading process and returns undefined.
   * Once response is received, the getter returns an array of employments (list items).
   * The method also initializes the taxcard (if personal ID is changed and valid).
   */
  public get otherEmployments(): EmploymentListItem[] | undefined {
    if (!this.current) {
      return undefined;
    }
    this.checkPersonalId();
    return this._otherEmployments == "loading" ? undefined : this._otherEmployments;
  }

  /**
   * Gets a description text for an employment relation
   * @param employment The employment relation to describe.
   */
  public getEmploymentDescription(employment: Employment | EmploymentListItem): string {
    if (EmploymentLogic.isListItem(employment)) {
      return EmploymentLogic.getDescription(employment.otherPartyInfo, employment.data.type, employment.data.pensionCalculation);
    }
    return EmploymentLogic.getDescription(employment.personalInfo, employment.info?.type, employment.info?.pensionCalculation);
  }

  /**
   * Gets a descriptive text on whether the employment is active or not.
   * @param employment Employment relation to get the text for.
   * @returns A text that describes whether the employment is active or not with the dates (start and end).
   */
  public getActiveText(employment: Employment | EmploymentListItem): string {
    return EmploymentLogic.getActiveText(employment);
  }

  /**
   * Populates worker with test data.
   * @param useRealPersonaIdRange If true, uses the real personal id range instead of artificial ID range ("keinohetu": last 3 numbers start with 9).
   */
  public populateWithTestData(useRealPersonaIdRange = false) {
    EmploymentLogic.populateWithTestData(this.current, useRealPersonaIdRange);
  }

  /**
   * Deletes a calculation row.
   * @param rowIndex - Zero based row index of the row that should be deleted.
   */
  public deleteSalaryDefaultsRow(rowIndex: number) {
    this.current.work.salaryDefaults.splice(rowIndex);
  }

  /** Returns true if the employer is a household employer (person as opposed to company). */
  public get isHousehold() {
    return this.sessionService.isInRole("person");
  }

  /** Worker wizard configuration */
  public getWizardSteps(): WizardStep[] {
    const commonSteps = [{
      title: "SALAXY.NG1.WorkerWizardComponent.step1.title",
      view: "salaxy-components/worker/EmploymentWizard/identity.html",
    },
    {
      title: "SALAXY.NG1.WorkerWizardComponent.step2.title",
      view: "salaxy-components/worker/EmploymentWizard/person.html",
    },
    {
      title: "SALAXY.NG1.WorkerWizardComponent.step3.title",
      view: "salaxy-components/worker/EmploymentWizard/type.html",
    }];
    if (this.isHousehold){
      return commonSteps;
    }
    return [
      ...commonSteps,
      {
        title: "SALAXY.NG1.WorkerWizardComponent.step4.title",
        view: "salaxy-components/worker/EmploymentWizard/work.html",
      },
      {
        title: "SALAXY.NG1.WorkerWizardComponent.step5.title",
        view: "salaxy-components/worker/EmploymentWizard/tax.html",
      }
    ];
  }

  /** Saves the taxcard and closes the dialog. */
  public saveAndClose(closeFunction: (result: any) => any) {
    let card = this.taxcard;
    if (this.current.info.taxcard != EmploymentRelationTaxcard.Manual
      || !card.card.kind || card.card.kind === TaxcardKind.Undefined || card.card.kind === TaxcardKind.Auto) {
      // For clarity, remove the taxcard if it is not manual or if there is no meaningful kind.
      card = null;
    }
    closeFunction({
      action: "ok",
      employment: this.current,
      taxcard: card,
    });
  }

  /**
   * Gets the contact information of the worker. Contact of the specific type will be created if one does not exist.
   * @param type Type of contact info.
   * @returns Contact object for data binding.
   */
  public getContact(type = ContactType.Default) {
    return EmploymentLogic.getContact(this.current, type, true);
  }

  /**
   * Returns true if the current personal ID is other identifier than Finnish Personal Identification Number (keinohetu).
   */
  public get isOtherIdentifier(){
    return EmploymentLogic.isPersonalIdOther(this.current.identity.personalId);
  }

  private checkPersonalId(): void {
    const personalId = this.current.identity.personalId;
    if (!personalId || !Validation.isPersonalIdFi(personalId)) {
      if (this._otherEmployments || this._otherEmploymentsLastPersonalId) {
        this._otherEmploymentsLastPersonalId = undefined;
        this._otherEmployments = undefined;
      }
      return;
    }
    if (this.taxcard?.card?.personalId !== personalId) {
      this.current.info.taxcard = EmploymentRelationTaxcard.Auto;
      this.taxcard = TaxCard2019Logic.getBlank(personalId, TaxcardKind.Auto);
    }
    if (this._otherEmploymentsLastPersonalId != this.current.identity.personalId) {
      this._otherEmploymentsLastPersonalId = this.current.identity.personalId;
      this._otherEmployments = "loading";
      this.api.getOData({ $filter: `otherPartyInfo/officialId eq '${ this.current.identity.personalId }'` }).then((result) => {
        this._otherEmployments = result.items.filter(x => x.id != this.current.id);
      });
    }
  }
}
